import { useState, useRef } from 'react';
import { compressImageToBase64, validateImageFile } from '../utils/imageCompression';

interface EmployeePhotoUploadProps {
  currentPhoto?: string | null;
  onPhotoChange: (base64: string | null) => void;
  disabled?: boolean;
}

/**
 * EmployeePhotoUpload component
 * Handles employee photo upload with automatic compression to 512x512px
 */
export default function EmployeePhotoUpload({
  currentPhoto,
  onPhotoChange,
  disabled = false
}: EmployeePhotoUploadProps) {
  const [preview, setPreview] = useState<string | null>(currentPhoto || null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    // Validate file
    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsUploading(true);
      
      // Compress and convert to base64 (512x512px)
      const base64 = await compressImageToBase64(file, 512);
      
      setPreview(base64);
      onPhotoChange(base64);
    } catch (err) {
      setError('Gagal memproses foto');
      console.error('Image compression error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    setPreview(null);
    onPhotoChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Photo
      </label>
      
      <div className="flex items-start gap-4">
        {/* Preview */}
        <div className="flex-shrink-0">
          {preview ? (
            <div className="relative w-32 h-32 border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
              <img
                src={preview}
                alt="Employee Photo"
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Upload Controls */}
        <div className="flex-1 space-y-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            disabled={disabled || isUploading}
            className="hidden"
          />
          
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClick}
              disabled={disabled || isUploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {isUploading ? 'Memproses...' : preview ? 'Ganti Foto' : 'Upload Foto'}
            </button>
            
            {preview && (
              <button
                type="button"
                onClick={handleRemovePhoto}
                disabled={disabled || isUploading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm"
              >
                Hapus
              </button>
            )}
          </div>
          
          <p className="text-xs text-gray-500">
            Format: JPG, PNG, WebP (Max 5MB)
            <br />
            Foto akan otomatis diresize ke 512x512px
          </p>
          
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}
